import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "../site/Motion";
import { COVERAGE_NOTE } from "../../data/content";

export const Statement = () => (
  <section className="bg-[#FDFBF7] py-24 sm:py-32" data-testid="home-statement">
    <div className="mx-auto max-w-5xl px-5 sm:px-8">
      <Reveal>
        <p className="font-serif-display text-3xl leading-[1.2] text-[#1E2621] sm:text-5xl sm:leading-[1.15]">
          Recovery isn't a straight line. We meet you where you are, with <span className="italic text-[#A9533A]">peers who understand</span>, counseling under clinical supervision, and a plan built around <span className="italic">your</span> goals.
        </p>
      </Reveal>
    </div>
  </section>
);

const STEPS = [
  { n: "01", t: "Reach out", d: "Call or send a short message. Tell us which service you're curious about and how you plan to pay." },
  { n: "02", t: "Assessment", d: "For Medicaid-covered services, care begins with an assessment." },
  { n: "03", t: "Your plan", d: "Together we build an individualized treatment plan tied to your personal recovery goals." },
  { n: "04", t: "Ongoing support", d: "Sessions in-person or by telehealth, individually or in a group." },
];

export const Process = () => (
  <section className="grain relative overflow-hidden bg-[#1B241E] py-24 text-[#F4F1EA] sm:py-32" data-testid="home-process">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
      <Reveal className="max-w-2xl">
        <Eyebrow light>How it starts</Eyebrow>
        <h2 className="font-serif-display mt-4 text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">Four steady steps. <span className="italic text-[#E9A88F]">No pressure.</span></h2>
      </Reveal>
      <div className="mt-14 grid gap-px overflow-hidden rounded-[2rem] bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08} className="h-full">
            <div className="group flex h-full flex-col bg-[#1B241E] p-8 transition-colors duration-500 hover:bg-[#232E27]" data-testid={`process-step-${s.n}`}>
              <span className="font-serif-display text-6xl italic text-[#C86D51] transition-transform duration-500 group-hover:-translate-y-1">{s.n}</span>
              <h3 className="font-serif-display mt-10 text-3xl">{s.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#C9CFC6]">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal><p className="mt-8 max-w-2xl text-sm text-[#C9CFC6]">{COVERAGE_NOTE}</p></Reveal>
    </div>
  </section>
);

const CREDS = ["CEO & Operating Manager", "Ohio Certified Peer Recovery Supporter", "Chemical Dependency Counselor Assistant (CDCA)", "Life Coach"];

export const Leader = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="bg-[#F4F1EA] py-24 sm:py-32" data-testid="home-leader">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-16 lg:px-12">
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] rounded-tl-[8rem] border border-[#E2DCD0]" data-testid="leader-portrait">
            <motion.img style={{ y }} src="/images/leadership-review.webp" alt="Surgevity Solutions leadership reviewing a client's plan together" className="absolute inset-0 h-full w-full scale-[1.15] object-cover object-top" />
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <Eyebrow>Leadership</Eyebrow>
            <h2 className="font-serif-display mt-4 text-5xl leading-none tracking-tight sm:text-7xl" data-testid="leader-name">Kayona Ward</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4A554D] md:text-lg">
              Kayona leads Surgevity Solutions and works directly with the people we serve, bringing together peer recovery support, substance use counseling and life coaching under one roof.
            </p>
          </Reveal>
          <ul className="mt-10 border-t border-[#D6CFC1]">
            {CREDS.map((c, i) => (
              <Reveal key={c} delay={i * 0.06}>
                <li className="group flex items-center justify-between border-b border-[#D6CFC1] py-5" data-testid={`leader-credential-${i}`}>
                  <span className="font-serif-display text-2xl text-[#1E2621] transition-transform duration-500 group-hover:translate-x-2 sm:text-3xl">{c}</span>
                  <span className="font-mono-ui text-xs text-[#A9533A]">0{i + 1}</span>
                </li>
              </Reveal>
            ))}
          </ul>
          <Reveal>
            <Link to="/contact" className="group mt-10 inline-flex items-center gap-2 text-sm font-medium" data-testid="leader-contact-link">
              Talk with Kayona's team <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
